import { useEffect, useMemo, useRef, useState } from "react";
import { FilterFieldIcon } from "./FilterFieldIcon";
import type { DashboardFilterState } from "./useDashboardFilters";
import { NbsDateRangeFilter } from "./NbsDateRangeFilter";
import { NbsYearRangeFilter } from "./NbsYearRangeFilter";
import { useStickyFiltersHeight } from "./useStickyFiltersHeight";

export type FilterTerritory = { id: string; label: string; kind: string };
export type FilterService = { id: string; libelle: string };
export type FilterIndicateur = { id: string; libelle: string };

export type NbsGlobalFiltersProps = {
  readonly state: DashboardFilterState;
  readonly setState: (patch: Partial<DashboardFilterState>) => void;
  readonly reset: () => void;
  readonly activeCount: number;
  readonly territories: readonly FilterTerritory[];
  readonly services: readonly FilterService[];
  readonly indicateurs: readonly FilterIndicateur[];
  readonly years: readonly number[];
  readonly showDatePicker?: boolean;
};

type MultiKey = "territories" | "serviceIds" | "indicateurIds";
type FieldOption = { value: string; label: string };
type FieldConfig = { key: MultiKey; label: string; selectId: string; options: FieldOption[] };

function toggle(list: readonly string[], value: string): string[] {
  return list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
}

/**
 * Global analytics filter bar — token/chip pattern inspired by Contentful,
 * Cloudflare and Outlook: a compact toolbar where each dimension is a
 * removable chip that opens a searchable checkbox popover, instead of raw
 * multi-line native list boxes.
 *
 * Native `<select multiple>` mirrors (visually hidden, same ids
 * `nbs-filter-territory` / `-service` / `-indicateur`) are kept wired to the
 * same state so accessibility tooling and end-to-end `selectOption` drivers
 * keep working — the URL-backed `useDashboardFilters` state stays the single
 * source of truth.
 */
export function NbsGlobalFilters({
  state,
  setState,
  reset,
  activeCount,
  territories,
  services,
  indicateurs,
  years,
  showDatePicker = false,
}: NbsGlobalFiltersProps) {
  const [openField, setOpenField] = useState<MultiKey | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef<HTMLDivElement | null>(null);
  useStickyFiltersHeight(rootRef);

  const SAVED_VIEWS: ReadonlyArray<{ label: string; patch: Partial<DashboardFilterState> }> = [
    { label: "Suivi trimestriel", patch: { fromYear: "2005", toYear: "2025", year: "2025" } },
    { label: "Période complète", patch: { fromYear: "2005", toYear: "2025" } },
    { label: "Instantané 2015", patch: { fromYear: "2015", toYear: "2015", year: "2015" } },
    { label: "Complétude 2024", patch: { fromYear: "2024", toYear: "2024", year: "2024" } },
  ];

  // The active view is derived from the current filter state (not just the last
  // click): a view is active when every field in its patch matches the state.
  const activeView =
    SAVED_VIEWS.find((v) =>
      (Object.entries(v.patch) as Array<[keyof DashboardFilterState, unknown]>).every(
        ([key, val]) => state[key] === val,
      ),
    ) ?? null;

  const fields = useMemo<FieldConfig[]>(
    () =>
      [
        {
          key: "territories" as const,
          label: "Territoire",
          selectId: "nbs-filter-territory",
          options: territories.map((t) => ({ value: t.id, label: `${t.label} (${t.kind})` })),
        },
        {
          key: "serviceIds" as const,
          label: "Service écosystémique",
          selectId: "nbs-filter-service",
          options: services.map((s) => ({ value: s.id, label: s.libelle })),
        },
        {
          key: "indicateurIds" as const,
          label: "Indicateur",
          selectId: "nbs-filter-indicateur",
          options: indicateurs.map((i) => ({ value: i.id, label: i.libelle })),
        },
      ].filter((f) => f.options.length > 0 || state[f.key].length > 0),
    [territories, services, indicateurs, state],
  );

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpenField(null);
        setAddOpen(false);
        setViewOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        setOpenField(null);
        setAddOpen(false);
        setViewOpen(false);
      }
    }
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  function summary(field: FieldConfig): string {
    const selected = state[field.key];
    if (selected.length === 0) return "";
    if (selected.length === 1) {
      return field.options.find((o) => o.value === selected[0])?.label ?? "1 sélectionné";
    }
    return `${selected.length} sélectionnés`;
  }

  function openPopover(key: MultiKey) {
    setQuery("");
    setAddOpen(false);
    setOpenField((cur) => (cur === key ? null : key));
  }

  function renderPopover(field: FieldConfig) {
    const selected = state[field.key];
    const q = query.trim().toLowerCase();
    const visibleOptions = q
      ? field.options.filter((o) => o.label.toLowerCase().includes(q))
      : field.options;
    return (
      <div className="nbs-filter-pop" role="dialog" aria-label={field.label}>
        <div className="nbs-filter-pop__searchwrap">
          <svg
            className="nbs-filter-pop__search-ico"
            width="15"
            height="15"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            aria-hidden="true"
          >
            <circle cx="11" cy="11" r="7" />
            <path d="m20 20-3-3" />
          </svg>
          <input
            className="nbs-filter-pop__search"
            type="text"
            placeholder="Rechercher…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
          />
          {query ? (
            <button
              type="button"
              className="nbs-filter-pop__clear"
              aria-label="Effacer la recherche"
              onClick={() => setQuery("")}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
              >
                <path d="M6 6l12 12M18 6 6 18" />
              </svg>
            </button>
          ) : null}
        </div>
        <div className="nbs-filter-pop__opts">
          {visibleOptions.map((o) => (
            <label className="nbs-filter-pop__opt" key={o.value}>
              <input
                type="checkbox"
                checked={selected.includes(o.value)}
                onChange={() =>
                  setState({
                    [field.key]: toggle(selected, o.value),
                  } as Partial<DashboardFilterState>)
                }
              />
              {o.label}
            </label>
          ))}
          {visibleOptions.length === 0 ? (
            <p className="nbs-filter-pop__empty">Aucun résultat</p>
          ) : null}
        </div>
        <div className="nbs-filter-pop__foot">
          <button
            type="button"
            className="nbs-filters__reset"
            onClick={() => setState({ [field.key]: [] } as Partial<DashboardFilterState>)}
          >
            Effacer
          </button>
          <button
            type="button"
            className="nbs-filter-pop__apply"
            onClick={() => setOpenField(null)}
          >
            Appliquer
          </button>
        </div>
      </div>
    );
  }

  const activeFields = fields.filter((f) => state[f.key].length > 0);
  const inactiveFields = fields.filter((f) => state[f.key].length === 0);
  const pendingField =
    openField && !activeFields.some((f) => f.key === openField)
      ? (fields.find((f) => f.key === openField) ?? null)
      : null;

  return (
    <div
      className="nbs-global-filters nbs-global-filters--bar nbs-filters-sticky"
      role="group"
      aria-label="Filtres globaux"
      ref={rootRef}
    >
      <div className="nbs-sr-only">
        {fields.map((field) => (
          <label key={field.selectId} htmlFor={field.selectId}>
            {field.label}
            <select
              id={field.selectId}
              multiple
              value={state[field.key]}
              onChange={(e) =>
                setState({
                  [field.key]: Array.from(e.target.selectedOptions).map((o) => o.value),
                } as Partial<DashboardFilterState>)
              }
            >
              {field.options.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
        ))}
      </div>

      <span className="nbs-filters__lead" aria-hidden="true">
        <svg
          width="15"
          height="15"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M3 5h18l-7 8v6l-4 2v-8L3 5Z" />
        </svg>
        Filtres
      </span>

      {activeFields.map((field) => (
        <div className="nbs-filter-token" key={field.key}>
          <button
            type="button"
            className="nbs-filter-token__body"
            aria-expanded={openField === field.key}
            onClick={() => openPopover(field.key)}
          >
            <span className="nbs-filter-token__key">{field.label} :</span>
            <span className="nbs-filter-token__val">{summary(field)}</span>
            <svg
              className="nbs-filter-token__caret"
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            >
              <path d="m6 9 6 6 6-6" />
            </svg>
          </button>
          <button
            type="button"
            className="nbs-filter-token__x"
            aria-label={`Retirer le filtre ${field.label}`}
            onClick={() => setState({ [field.key]: [] } as Partial<DashboardFilterState>)}
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
            >
              <path d="M6 6l12 12M18 6 6 18" />
            </svg>
          </button>
          {openField === field.key ? renderPopover(field) : null}
        </div>
      ))}

      {pendingField ? (
        <div className="nbs-filter-token">
          <button
            type="button"
            className="nbs-filter-token__body"
            aria-expanded="true"
            onClick={() => openPopover(pendingField.key)}
          >
            <span className="nbs-filter-token__key">{pendingField.label} :</span>
            <span className="nbs-filter-token__val nbs-filter-token__val--empty">tous</span>
            <svg
              className="nbs-filter-token__caret"
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            >
              <path d="m6 9 6 6 6-6" />
            </svg>
          </button>
          {renderPopover(pendingField)}
        </div>
      ) : null}

      {inactiveFields.length > 0 ? (
        <div className="nbs-filter-token nbs-filter-token--add">
          <button
            type="button"
            className="nbs-add-filter"
            aria-expanded={addOpen}
            onClick={() => {
              setOpenField(null);
              setAddOpen((v) => !v);
            }}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            >
              <path d="M5 12h14M12 5v14" />
            </svg>
            Ajouter un filtre
          </button>
          {addOpen ? (
            <div className="nbs-filter-pop" role="menu">
              <div className="nbs-filter-pop__title">Filtrer par</div>
              {inactiveFields.map((field) => (
                <button
                  key={field.key}
                  type="button"
                  className="nbs-filter-pop__row"
                  role="menuitem"
                  onClick={() => {
                    setAddOpen(false);
                    openPopover(field.key);
                  }}
                >
                  <span className="nbs-filter-pop__row-main">
                    <FilterFieldIcon fieldKey={field.key} />
                    {field.label}
                  </span>
                  <span className="nbs-filter-pop__count">{field.options.length}</span>
                </button>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}

      <span className="nbs-filters__spacer" />

      <div className="nbs-filters__inline">
        <NbsYearRangeFilter
          years={years}
          fromYear={state.fromYear}
          toYear={state.toYear}
          year={state.year}
          onChange={setState}
        />
        <label className="nbs-filters__metric">
          Métrique sols
          <select
            value={state.metric}
            onChange={(e) =>
              setState({ metric: e.target.value === "part" ? "part" : "superficie" })
            }
          >
            <option value="superficie">Hectares</option>
            <option value="part">Pourcentage</option>
          </select>
        </label>
        <NbsDateRangeFilter
          visible={showDatePicker}
          dateFrom={state.dateFrom ?? ""}
          dateTo={state.dateTo ?? ""}
          onChange={(patch) => setState(patch)}
        />
      </div>

      <div className="nbs-filter-token nbs-saved-view-wrap">
        <button
          type="button"
          className="nbs-saved-view"
          aria-expanded={viewOpen}
          onClick={() => {
            setOpenField(null);
            setAddOpen(false);
            setViewOpen((v) => !v);
          }}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinejoin="round"
          >
            <path d="M5 4h14v16l-7-4-7 4V4Z" />
          </svg>
          Vue : {activeView ? activeView.label : "personnalisée"}
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
          >
            <path d="m6 9 6 6 6-6" />
          </svg>
        </button>
        {viewOpen ? (
          <div className="nbs-filter-pop nbs-filter-pop--right" role="menu">
            <div className="nbs-filter-pop__title">Vues enregistrées</div>
            {SAVED_VIEWS.map((v) => {
              const isActive = activeView?.label === v.label;
              return (
                <button
                  key={v.label}
                  type="button"
                  className={`nbs-filter-pop__row${isActive ? " nbs-filter-pop__row--active" : ""}`}
                  role="menuitemradio"
                  aria-checked={isActive}
                  onClick={() => {
                    setState(v.patch);
                    setViewOpen(false);
                  }}
                >
                  {v.label}
                  {isActive ? (
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M5 12l5 5L20 6" />
                    </svg>
                  ) : null}
                </button>
              );
            })}
          </div>
        ) : null}
      </div>

      <button
        type="button"
        className="nbs-filters__reset"
        onClick={reset}
        disabled={activeCount === 0}
      >
        Réinitialiser
      </button>
    </div>
  );
}
